/**
 * Sync two carousels by mirroring movement requests in both directions.
 * - When the other carousel requests movement, mirror it here with same velocity/type.
 * - When this carousel requests movement, push that request to the other carousel.
 */
export default class SyncWith {
	/**
	 * @constructor
	 * @param {Object} ctx - the carousel context that will be synced with another
	 */
	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;
		_.otherCarousel = null;
		_.isSyncing = false;

		// define all event handlers in one object
		_.handlers = {
			// handler for other carousel's movement requested event
			otherMovementRequested: ({ index, velocity, movementType }) => {
				if (_.isSyncing) return; // we initiated this, ignore

				const currentIndex = _.ctx.store.getState().renderIndex;
				if (currentIndex === index) return;

				// sync to other carousel's movement with same velocity/type
				_.isSyncing = true;
				setTimeout(() => {
					_.ctx.commands.goToSlide(index, velocity, movementType);
					_.isSyncing = false;
				}, 1);
			},

			// handler for carousel's optionsChanged event
			optionsChanged: (data) => {
				// check options to see if we still have SyncWith
				// disconnect or connect to new carousel
				_.reInit();
			},

			// handler for this carousel's movement requested event
			movementRequested: ({ index, velocity, movementType }) => {
				if (_.isSyncing) return; // prevent re-entry from sync loop
				if (!_.otherCarousel) return;

				// push movement to other carousel with same velocity/type
				_.isSyncing = true;
				setTimeout(() => {
					_.otherCarousel.goToSlide(index, velocity, movementType);
					_.isSyncing = false;
				}, 1);
			},
		};

		_.init();
	}

	/**
	 * query the other carousel from the DOM
	 */
	queryDOM() {
		const _ = this;
		const options = _.ctx.store.getOptions();
		const otherCarouselId = options.syncWith;

		// exit if no value
		if (!otherCarouselId) return false;

		// get syncWith value from options
		_.otherCarousel = document.querySelector(otherCarouselId);

		// can't find it
		if (!_.otherCarousel) return false;

		return true;
	}

	/**
	 * initialize the sync functionality
	 */
	init() {
		this.bindEvents();
		this.setupConnection();
	}

	/**
	 * setup the connection to the other carousel
	 */
	setupConnection() {
		const _ = this;

		if (!_.queryDOM()) return;

		// give DOM a few ms to load the custom elements
		setTimeout(() => {
			_.otherCarousel.on(_.ctx.events.movement.requested, _.handlers.otherMovementRequested);
		}, 100);
	}

	/**
	 * reinitialize on carousel option changes
	 */
	reInit() {
		const _ = this;
		// cleanup existing connection
		if (_.otherCarousel) {
			_.otherCarousel.off(_.ctx.events.movement.requested, _.handlers.otherMovementRequested);
			_.otherCarousel = null;
		}
		// reinitialize connection (don't rebind events)
		_.setupConnection();
	}

	/**
	 * bind carousel events to handlers
	 */
	bindEvents() {
		const _ = this;
		// bind to carousel events using new event system
		_.ctx.emitter.on(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
		_.ctx.emitter.on(_.ctx.events.movement.requested, _.handlers.movementRequested);
	}

	/**
	 * destroy the sync, unbinding all events
	 */
	destroy() {
		const _ = this;

		// unbind from carousel events
		_.ctx.emitter.off(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
		_.ctx.emitter.off(_.ctx.events.movement.requested, _.handlers.movementRequested);

		// unbind from other carousel events
		if (_.otherCarousel) {
			_.otherCarousel.off(_.ctx.events.movement.requested, _.handlers.otherMovementRequested);
		}

		_.otherCarousel = null;
	}
}
